import React, { PropsWithChildren, useEffect, useState } from 'react';
import { OrderedMap } from 'immutable';
import { apiRequest } from '@support/api';
import { WorkspaceContext, WorkspaceContextSchema } from './WorkspaceContext';
import Workspace, { WorkspaceMember, WorkspaceAPIItem } from '@models/Workspace';
import Occasion from '@support/Occasion';
import { useAuthContext } from '@support/hooks/use-auth-context';
import { ID } from '@app/types';

interface WorkspaceMemberAPIResponse {
  id: number;
  is_admin: boolean;
  avatar_url: string | null;
  email: string;
  first_name: string;
  last_name: string;
}

export function WorkspaceProvider({ children }: PropsWithChildren<{}>) {
  const { isReady, user } = useAuthContext();
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace>(null);
  const [workspaces, setWorkspaces] = useState<OrderedMap<ID, Workspace>>(OrderedMap());

  useEffect(() => {
    if (isReady && user) {
      // If this fails, activeWorkspace remains null
      apiRequest<WorkspaceAPIItem>('GET', '/workspaces/active/', undefined, { apiVersion: 'v2' })
        .then((data) =>
          setActiveWorkspace(new Workspace({ ...(data || {}), updated: new Occasion() })),
        )
        .catch((_) => {});
    }
  }, [user, isReady]);

  useEffect(() => {
    if (isReady && user) {
      apiRequest<WorkspaceAPIItem[]>('GET', '/workspaces/', undefined, { apiVersion: 'v2' }).then(
        (data) => {
          setWorkspaces(
            OrderedMap(
              (data || []).map((workspace) => [String(workspace.id), new Workspace(workspace)]),
            ),
          );
        },
      );
    }
  }, [user, isReady]);

  useEffect(() => {
    if (activeWorkspace?.isLoaded) {
      apiRequest<WorkspaceMemberAPIResponse[]>('GET', '/workspace/members/', undefined, {
        apiVersion: 'v2',
      }).then((members) => {
        setActiveWorkspace(
          activeWorkspace.copyWith({
            members: OrderedMap(
              members.map((member) => [String(member.id), new WorkspaceMember(member)]),
            ),
          }),
        );
      });
    }
  }, [activeWorkspace?.updated]);

  const value: WorkspaceContextSchema = {
    activeWorkspace,
    workspaces,
    userIsAdmin: activeWorkspace?.isAdmin,
    updateActiveWorkspace: (updates: Partial<Workspace>) =>
      setActiveWorkspace(activeWorkspace.copyWith(updates)),
    updateWorkspaces: (updated: OrderedMap<ID, Workspace>) => setWorkspaces(updated),
    isMultiPlayer: activeWorkspace && activeWorkspace.members.size > 1,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
